import { Component } from '@theme/component';

/**
 * Gates the standard cart checkout button behind a terms-of-service checkbox.
 *
 * Clicking the checkout button while the checkbox is unchecked opens a modal
 * instead of submitting. Accepting in the modal checks the box and re-triggers
 * checkout. Express checkout buttons are intentionally left untouched.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox - The terms acceptance checkbox.
 * @property {HTMLDialogElement} dialog - The terms modal dialog element.
 * @property {HTMLButtonElement} checkout - The standard checkout submit button.
 *
 * @extends Component<Refs>
 */
class TermsAgreement extends Component {
  requiredRefs = ['checkbox', 'dialog', 'checkout'];

  // Blocks the standard checkout and opens the modal when terms are not accepted.
  // Runs in the document capture phase, so preventDefault stops the form submit.
  maybeBlockCheckout = (event) => {
    if (this.refs.checkbox.checked) return;

    event.preventDefault();
    event.stopPropagation();
    this.refs.dialog.showModal();
  };

  // Closes the modal without accepting.
  closeModal = () => {
    this.refs.dialog.close();
  };

  // Closes the modal when the backdrop (the dialog element itself) is clicked.
  closeOnBackdrop = (event) => {
    if (event.target === this.refs.dialog) this.refs.dialog.close();
  };

  // Accepts the terms: checks the box, closes the modal, and proceeds to the
  // standard checkout by re-triggering the now-unblocked checkout button.
  acceptAndContinue = () => {
    this.refs.checkbox.checked = true;
    this.refs.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    this.refs.dialog.close();
    this.refs.checkout.click();
  };
}

if (!customElements.get('terms-agreement')) {
  customElements.define('terms-agreement', TermsAgreement);
}
